import { DrawerNavigation } from '../components/navigation/Drawer/DrawerNavigation';
import { BarNavigation } from '../components/navigation/Bar/BarNavigation';
import { useWindowDimensions, Animated } from 'react-native';
import Cart from '../screens/Main/Cart';
import { Profile } from '../screens/Main/Profile';
import { navigationStore } from '../app/stores/Navigation';
import { userStore } from '../app/stores/User';
import { Drawer } from '@mui/material';
import { useEffect, useState, useRef, useMemo } from 'react';
import { appConfigStore } from '../app/stores/AppConfig';
import { useComponentMount } from '../services/InitializationService';
import { darkBackgroundSecondary, lightBackgroundSecondary } from '../config/theme';

export const isLarge = () => {
	const windowDimensions = useWindowDimensions();
	const screenWidth = windowDimensions.width;
	return screenWidth > 870;
};

export const HomeStack = () => {
	useComponentMount('HomeStack');
	const { cartDrawerOpen, updateCartDrawerOpen, profileDrawerOpen, updateProfileDrawerOpen } = navigationStore((state) => state);
	const { authenticated } = userStore((state) => state.user);
	const { darkMode } = appConfigStore((state) => state);
	const [darkModeDrawerOpen, setDarkModeDrawerOpen] = useState(false);
	const [transitionText, setTransitionText] = useState('');
	const [targetMode, setTargetMode] = useState(false);
	const [animationComplete, setAnimationComplete] = useState(true);
	const contentFadeAnim = useRef(new Animated.Value(0)).current;
	const scaleAnim = useRef(new Animated.Value(0.8)).current;
	const spinnerRotateAnim = useRef(new Animated.Value(0)).current;
	const prevDarkMode = useRef(darkMode);
	const hasCheckedAuthUrl = useRef(false);

	// Check URL for /login or /register paths and open profile drawer if not authenticated
	useEffect(() => {
		if (hasCheckedAuthUrl.current) return;
		
		if (typeof window !== 'undefined') {
			const path = window.location.pathname.toLowerCase();
			if (path === '/login' || path === '/register') {
				// Open profile drawer if user navigates to login/register URL
				if (!authenticated) {
					updateProfileDrawerOpen(true);
				}
				// Redirect to shop
				window.history.replaceState({}, '', '/shop');
				hasCheckedAuthUrl.current = true;
			}
		}
	}, [authenticated, updateProfileDrawerOpen]);

	// Create a rotation interpolation for the spinner effect
	const spinnerRotation = spinnerRotateAnim.interpolate({
		inputRange: [0, 1],
		outputRange: ["0deg", "360deg"]
	});

	// Function to handle theme transition
	const handleThemeTransition = () => {
		// Start transition
		setTargetMode(darkMode);
		setTransitionText(darkMode ? "Going Dark" : "Lightening the Mood");
		setAnimationComplete(false);

		// Reset animations
		contentFadeAnim.setValue(0);
		scaleAnim.setValue(0.8);
		spinnerRotateAnim.setValue(0);

		// Open drawer immediately
		setDarkModeDrawerOpen(true);

		// Create sequential animation
		Animated.sequence([
			// Allow drawer to open fully
			Animated.delay(800),

			// Start content fade-in and scale-up together
			Animated.parallel([
				// Fade in content
				Animated.timing(contentFadeAnim, {
					toValue: 1,
					duration: 1200,
					useNativeDriver: true,
				}),

				// Scale up content
				Animated.timing(scaleAnim, {
					toValue: 1,
					duration: 1500,
					useNativeDriver: true,
				}),

				// Rotate spinner
				Animated.timing(spinnerRotateAnim, {
					toValue: 2,  // Two full rotations
					duration: 2000,
					useNativeDriver: true,
				})
			]),

			// Keep animation visible for longer
			Animated.delay(3000),

			// Fade out content before closing
			Animated.timing(contentFadeAnim, {
				toValue: 0,
				duration: 800,
				useNativeDriver: true,
			})
		]).start(() => {
			// Animation complete - close drawer
			setDarkModeDrawerOpen(false);
			setAnimationComplete(true);
		});
	};

	// Detect darkMode changes and trigger transition drawer
	useEffect(() => {
		if (darkMode !== prevDarkMode.current && animationComplete) {
			handleThemeTransition();
		}

		prevDarkMode.current = darkMode;
	}, [darkMode]);

	const styleBasedOnScreenSize = () => {
		const radius = 14;
		if (isLarge()) {
			return {
				minWidth: "40%",
				maxWidth: "40%",
				borderTopLeftRadius: radius,
				borderBottomLeftRadius: radius,
			};
		} else {
			return {
				minHeight: "85%",
				maxHeight: "85%",
				borderTopLeftRadius: radius,
				borderTopRightRadius: radius,
			};
		}
	};

	// Memoize the Cart and Profile components to prevent unnecessary re-renders
	// Note: Empty dependency array means these only render once when drawer opens
	const MemoizedCart = useMemo(() => <Cart />, [cartDrawerOpen]);
	const MemoizedProfile = useMemo(() => <Profile />, [profileDrawerOpen]);

	return (
		<>
			{isLarge() ? <BarNavigation /> : <DrawerNavigation />}

			{/* Cart Drawer */}
			<Drawer
				variant="temporary"
				open={cartDrawerOpen}
				onClose={() => updateCartDrawerOpen(false)}
				anchor={isLarge() ? "right" : "bottom"}
				PaperProps={{
					sx: styleBasedOnScreenSize()
				}}
			>
				{MemoizedCart}
			</Drawer>

			{/* Profile Drawer */}
			<Drawer
				variant="temporary"
				open={profileDrawerOpen}
				onClose={() => updateProfileDrawerOpen(false)}
				anchor={isLarge() ? "right" : "bottom"}
				PaperProps={{
					sx: styleBasedOnScreenSize(),
				}}
			>
				{MemoizedProfile}
			</Drawer>
		</>
	);
};
